import { Box, Skeleton } from '@ltvco/refresh-lib/theme';
import React from 'react';

import { ViewToggle, type ViewType } from '../ViewToggle';

import {
  HeaderContainer,
  ResultsInfoContainer,
  ResultsCountText,
  SearchQueryText,
  FiltersRow,
  FiltersLabel,
  FilterItem,
  FilterSeparator,
  ViewToggleContainer,
  SkeletonContainer,
} from './components';

export interface AppliedFilter {
  label: string;
  value: string;
}

export interface SearchResultsHeaderProps {
  resultsCount: number;
  searchQuery: string;
  appliedFilters?: AppliedFilter[];
  loading?: boolean;
  currentView?: ViewType;
  onViewChange?: (view: ViewType) => void;
  showAppliedFilters?: boolean;
  showViewToggle?: boolean;
}

export const SearchResultsHeader: React.FC<SearchResultsHeaderProps> = ({
  resultsCount,
  searchQuery,
  appliedFilters = [],
  loading = false,
  currentView = 'grid',
  onViewChange,
  showAppliedFilters = true,
  showViewToggle = true,
}) => {
  const formatResultsCount = (count: number): string => {
    return count.toLocaleString();
  };

  const shouldShowAppliedFilters =
    showAppliedFilters && appliedFilters.length > 0;

  if (loading) {
    return (
      <HeaderContainer>
        <SkeletonContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rounded" width={280} height={24} />
            <Skeleton variant="rounded" width={200} height={32} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="rounded" width={100} height={20} />
            <Skeleton variant="rounded" width={150} height={20} />
            <Skeleton variant="rounded" width={200} height={20} />
          </Box>
        </SkeletonContainer>
        {showViewToggle && (
          <ViewToggleContainer>
            <Skeleton variant="rounded" width={140} height={40} />
          </ViewToggleContainer>
        )}
      </HeaderContainer>
    );
  }

  return (
    <HeaderContainer>
      <ResultsInfoContainer>
        {/* Results count and search query */}
        <Box>
          <ResultsCountText>
            <span className="label">We Found </span>
            <span className="count">{formatResultsCount(resultsCount)}</span>
            <span className="label"> cases found for:</span>
          </ResultsCountText>
          <SearchQueryText>{searchQuery}</SearchQueryText>
        </Box>

        {/* Applied filters */}
        {shouldShowAppliedFilters && (
          <FiltersRow>
            <FiltersLabel>Your search —</FiltersLabel>
            {appliedFilters.map((filter, index) => (
              <Box key={`${filter.label}-${index}`} component="span">
                <FilterItem>
                  {filter.label}: {filter.value}
                </FilterItem>
                {index < appliedFilters.length - 1 && (
                  <FilterSeparator>›</FilterSeparator>
                )}
              </Box>
            ))}
          </FiltersRow>
        )}
      </ResultsInfoContainer>

      {/* View toggle */}
      {showViewToggle && onViewChange && (
        <ViewToggleContainer>
          <ViewToggle currentView={currentView} onViewChange={onViewChange} />
        </ViewToggleContainer>
      )}
    </HeaderContainer>
  );
};
